import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Box,
  useTheme,
  useMediaQuery,
  Avatar,
  Divider,
  Tooltip
} from '@mui/material';
import {
  Menu,
  LayoutDashboard,
  Users,
  LogOut,
  DollarSign,
  Trophy,
  Gift,
  Mailbox,
  CalendarCheck
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { styled } from '@mui/material/styles';

const drawerWidth = 280;

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  width: drawerWidth,
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    width: drawerWidth,
    boxSizing: 'border-box',
    background: '#141926',
    color: '#fff',
    borderRight: '1px solid rgba(255,255,255,0.06)',
    boxShadow: '4px 0 24px rgba(0, 0, 0, 0.2)'
  },
}));

const StyledListItem = styled(ListItem)(({ theme, selected }) => ({
  margin: '8px 16px',
  borderRadius: '12px',
  background: selected 
    ? 'rgba(124, 58, 237, 0.15)' 
    : 'transparent',
  color: selected ? '#7C3AED' : '#fff',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    background: 'rgba(124, 58, 237, 0.1)',
  },
  '& .MuiListItemIcon-root': {
    color: selected ? '#7C3AED' : '#fff',
    transition: 'transform 0.3s ease',
  },
  '&:hover .MuiListItemIcon-root': {
    transform: 'scale(1.1)',
  },
  '& .MuiListItemText-root': {
    color: selected ? '#7C3AED' : '#fff',
  }
}));

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  width: '100%',
  marginLeft: 0,
  [theme.breakpoints.up('sm')]: {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: drawerWidth,
  },
  background: 'rgba(17, 24, 39, 0.8)',
  backdropFilter: 'blur(10px)',
  color: '#fff',
  boxShadow: 'none',
  borderBottom: '1px solid rgba(124, 58, 237, 0.2)'
}));

const UserAvatar = styled(Avatar)(({ theme }) => ({
  width: 48,
  height: 48,
  background: '#7C3AED',
  border: '1px solid rgba(255,255,255,0.06)',
}));

const AdminLayout = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  // Verificar se é admin
  if (user.tipo !== 'admin') {
    navigate('/login');
    return null;
  }

  const menuItems = [
    { text: 'Dashboard', icon: <LayoutDashboard size={20} strokeWidth={1.75} />, path: '/admin' },
    { text: 'Usuários', icon: <Users size={20} strokeWidth={1.75} />, path: '/admin-ftm' },
    { text: 'Vendas', icon: <DollarSign size={20} strokeWidth={1.75} />, path: '/admin-ftm/vendas' },
    { text: 'Top 50 Clientes', icon: <Trophy size={20} strokeWidth={1.75} />, path: '/admin-ftm/top-clientes' },
    { text: 'Afiliados', icon: <Gift size={20} strokeWidth={1.75} />, path: '/admin-ftm/afiliados' },
    { text: 'Agendamentos', icon: <CalendarCheck size={20} strokeWidth={1.75} />, path: '/admin-ftm/agendamentos' },
    { text: 'Email Broadcast', icon: <Mailbox size={20} strokeWidth={1.75} />, path: '/admin-ftm/broadcast' }
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const drawer = (
    <div>
      <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <UserAvatar src={user.foto}>
          {user.nome?.charAt(0)?.toUpperCase()}
        </UserAvatar>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#fff' }}>
            {user.nome || 'Administrador'}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            {user.email}
          </Typography>
        </Box>
      </Box>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
      <List>
        {menuItems.map((item) => (
          <Tooltip 
            key={item.text} 
            title={item.text} 
            placement="right"
            arrow
          >
            <StyledListItem 
              button 
              onClick={() => {
                navigate(item.path);
                if (isMobile) setMobileOpen(false);
              }}
              selected={location.pathname === item.path}
            >
              <ListItemIcon>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </StyledListItem>
          </Tooltip>
        ))}
        <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.1)' }} />
        <Tooltip title="Sair" placement="right" arrow>
          <StyledListItem button onClick={handleLogout}>
            <ListItemIcon>
              <LogOut size={20} strokeWidth={1.75} />
            </ListItemIcon>
            <ListItemText primary="Sair" />
          </StyledListItem>
        </Tooltip>
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex', background: '#111827' }}>
      <StyledAppBar position="fixed">
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <Menu size={20} strokeWidth={1.75} />
          </IconButton>
          <Typography variant="h6" sx={{ color: '#7C3AED', fontWeight: 600 }}>
            Painel de Administração
          </Typography>
        </Toolbar>
      </StyledAppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              background: '#141926',
              color: '#fff'
            },
          }}
        >
          {drawer}
        </Drawer>
        <StyledDrawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
          }}
          open
        >
          {drawer}
        </StyledDrawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          marginTop: '64px'
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default AdminLayout; 